import React from 'react';
import useBundleTranslation from 'i18n';
import { VIEW_W } from './layout';
import { REPORT_ACTIVE_STROKE } from './nodeTypeColors';

const H = 24;
const Y = 6;
const DOT_R = 4;
const DOT_TEXT_GAP = 6;
const BETWEEN_GAP = 14;
const PAD_X = 12;
// Approximate char width at fontSize=11px
const CHAR_W = 6.2;
const RING_STROKE_W = 2;

export default function Legend({
    typeColors,
    reportLensActive = false,
}: {
    typeColors: Map<string, string>;
    reportLensActive?: boolean;
}) {
    const { t } = useBundleTranslation('components/common/knowledge-graph/panel');
    const entries = [...typeColors.entries()];
    if (!entries.length) return null;

    const items = entries.map(([type, color]) => ({ key: type, label: type, color, ring: false }));
    // With a lens on, the outline is doing work in the diagram, so the legend has to explain it.
    if (reportLensActive) {
        items.push({
            key: 'in_report',
            label: t('legend_in_report', 'In this report'),
            color: '#9aa0a8',
            ring: true,
        });
    }

    const measured = items.map((item) => ({
        ...item,
        itemW: DOT_R * 2 + DOT_TEXT_GAP + item.label.length * CHAR_W,
    }));

    const totalW = measured.reduce((sum, item) => sum + item.itemW, 0) + (measured.length - 1) * BETWEEN_GAP;
    const startX = (VIEW_W - totalW) / 2;

    let curX = startX;
    const positioned = measured.map((item) => {
        const x = curX;
        curX += item.itemW + BETWEEN_GAP;
        return { ...item, x };
    });

    return (
        <g>
            <rect
                x={startX - PAD_X}
                y={Y}
                width={totalW + PAD_X * 2}
                height={H}
                rx={4}
                fill="white"
                stroke="#e0e4ec"
                strokeWidth={1}
            />
            {positioned.map(({ key, label, color, ring, x }) => (
                <g key={key} data-test={ring ? 'kg_graph_legend_in_report' : undefined}>
                    <circle
                        cx={x + DOT_R}
                        cy={Y + H / 2}
                        r={DOT_R}
                        fill={ring ? '#fff' : color}
                        stroke={ring ? REPORT_ACTIVE_STROKE : undefined}
                        strokeWidth={ring ? RING_STROKE_W : undefined}
                    />
                    <text x={x + DOT_R * 2 + DOT_TEXT_GAP} y={Y + H / 2 + 4} fontSize={11} fill="#374151">
                        {label}
                    </text>
                </g>
            ))}
        </g>
    );
}
